import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowDown,
  ArrowUpRight,
  BarChart3,
  Link2,
  QrCode,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { useKernel } from "./Shortener/hooks/AIKernel";
import Shortener from "./Shortener";
import "./Home.css";

const Home = () => {
  const { agentState, reportBehavior } = useKernel();
  const simplified = agentState.ui.simplifyAnimations;

  return (
    <main className={`home-page${simplified ? " home-page--calm" : ""}`}>
      <section className="home-hero">
        <div className="home-hero__copy">
          <div className="home-eyebrow">
            <span className="home-eyebrow__dot" />
            A clearer way to share
          </div>
          <h1>
            Make every link
            <br />
            <span>feel intentional.</span>
          </h1>
          <p className="home-hero__description">
            Turn long URLs into clean, shareable links. Keep your recent links
            close, create a QR code when you need one, and get helpful guidance
            along the way.
          </p>
          <div className="home-hero__actions">
            <a
              className={`home-primary-button${agentState.ui.highlightCTAs ? " cta-highlight-glow" : ""}`}
              href="#link-studio"
              onClick={() => reportBehavior("intent_hero_shorten", 3, "interest")}
            >
              Shorten a link <ArrowDown size={17} aria-hidden="true" />
            </a>
            <Link
              className="home-secondary-link"
              to="/features"
              onClick={() => reportBehavior("intent_hero_features", 1, "interest")}
            >
              Explore the features <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <div className="home-hero__note">
            <ShieldCheck size={16} aria-hidden="true" />
            Your recent links stay available in this browser.
          </div>
        </div>

        <motion.div
          className="home-preview"
          initial={simplified ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          aria-label="Linkpin product preview"
        >
          <div className="home-preview__top">
            <div>
              <span className="home-preview__overline">LINKPIN STUDIO</span>
              <h2>A little more clarity.</h2>
            </div>
            <span className="home-preview__sparkle">
              <Sparkles size={17} aria-hidden="true" />
            </span>
          </div>
          <div className="home-preview__link">
            <span className="home-preview__link-icon">
              <Link2 size={17} aria-hidden="true" />
            </span>
            <span className="home-preview__link-text">
              <strong>your.link</strong>
              <small>One link, ready to share</small>
            </span>
            <ArrowUpRight size={17} aria-hidden="true" />
          </div>
          <div className="home-preview__footer">
            <span><QrCode size={15} aria-hidden="true" /> QR included</span>
            <span><BarChart3 size={15} aria-hidden="true" /> Link history</span>
          </div>
          <div className="home-preview__glow" />
        </motion.div>
      </section>

      <section className="home-studio" id="link-studio" aria-labelledby="studio-heading">
        <div className="home-section-heading">
          <div>
            <span className="home-section-heading__eyebrow">YOUR LINK WORKSPACE</span>
            <h2 id="studio-heading">Start with a link.</h2>
          </div>
          <span className="home-studio__status">
            <span className="home-eyebrow__dot" />
            Ready when you are
          </span>
        </div>
        <Shortener />
      </section>

      <section className="home-benefits" aria-label="Linkpin essentials">
        <article className="home-benefit">
          <span className="home-benefit__icon"><Zap size={19} aria-hidden="true" /></span>
          <div>
            <h3>Simple by design</h3>
            <p>Paste a destination and create a share-ready short link.</p>
          </div>
        </article>
        <article className="home-benefit">
          <span className="home-benefit__icon"><QrCode size={19} aria-hidden="true" /></span>
          <div>
            <h3>QR, right there</h3>
            <p>Open a QR code for a shortened link and download it.</p>
          </div>
        </article>
        <article className="home-benefit">
          <span className="home-benefit__icon"><Sparkles size={19} aria-hidden="true" /></span>
          <div>
            <h3>A more adaptive experience</h3>
            <p>The Linkpin guide uses the local AIKernel to tailor helpful tips.</p>
          </div>
        </article>
      </section>
    </main>
  );
};

export default Home;
